import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getApiUrl } from '../config/api';
import './SearchResults.css';

function SearchResults() {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get('keyword');
  const [allResults, setAllResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBoard, setSelectedBoard] = useState('all');
  const [boards, setBoards] = useState([]);

  useEffect(() => {
    if (!keyword) {
      setLoading(false);
      return;
    }

    const fetchResults = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${getApiUrl()}/search?keyword=${encodeURIComponent(keyword)}`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const posts = data.posts || [];
        setAllResults(posts);
        setFilteredResults(posts);

        // 게시판 순서 정의
        const boardOrder = {
          'notice': { order: 1, name: '알림판' },
          'sermon': { order: 2, name: '설교모음' },
          'mark': { order: 3, name: '매일묵상' },
          'comment': { order: 4, name: '설교비평' },
          'berg': { order: 5, name: '판넨베르크 아카이브' },
          'current': { order: 6, name: '세상읽기' },
          'free': { order: 7, name: '사랑채' },
          'kimhr': { order: 8, name: '김혜란' },
          'fieldpath': { order: 9, name: '들길' },
          'india': { order: 10, name: '김현혁' },
          'theo': { order: 11, name: '정용섭' },
          'support': { order: 12, name: '후원안내' }
        };

        // 게시판 목록 추출 (중복 제거 및 정렬)
        const uniqueBoards = [...new Set(posts.map(post => post.board_id))]
          .map(boardId => ({
            id: boardId,
            name: boardOrder[boardId]?.name || boardId,
            order: boardOrder[boardId]?.order || 999
          }))
          .sort((a, b) => a.order - b.order);

        setBoards(uniqueBoards);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [keyword]);

  const handleBoardFilter = (boardId) => {
    setSelectedBoard(boardId);
    if (boardId === 'all') {
      setFilteredResults(allResults);
    } else {
      setFilteredResults(allResults.filter(post => post.board_id === boardId));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const str = String(dateStr);
    const year = str.substring(0, 4);
    const month = str.substring(4, 6);
    const day = str.substring(6, 8);
    return `${year}-${month}-${day}`;
  };

  if (loading) {
    return (
      <div id="contentBody" style={{display: 'block', padding: '30px 40px'}}>
        <div style={{background: '#fff', borderRadius: '10px', padding: '30px'}}>
          <h2 style={{fontSize: '20px', marginBottom: '20px'}}>검색 결과</h2>
          <p>"{keyword}" 검색 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div id="contentBody" style={{display: 'block', padding: '30px 40px'}}>
      <div style={{background: '#fff', borderRadius: '10px', padding: '30px'}}>
        {/* 검색 헤더 */}
        <div style={{marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #748320'}}>
          <h2 style={{fontSize: '20px', color: '#333', margin: 0}}>검색 결과</h2>
          <p style={{margin: '10px 0 0 0', color: '#666'}}>"{keyword}" 검색 결과 {allResults.length}건</p>
        </div>

        {/* 게시판 필터 네비게이션 */}
        {boards.length > 0 && (
          <div style={{marginBottom: '20px', display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
            <button
              onClick={() => handleBoardFilter('all')}
              style={{
                padding: '8px 16px',
                background: selectedBoard === 'all' ? '#748320' : '#f0f0f0',
                color: selectedBoard === 'all' ? '#fff' : '#333',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              전체 ({allResults.length})
            </button>
            {boards.map((board) => {
              const count = allResults.filter(post => post.board_id === board.id).length;
              return (
                <button
                  key={board.id}
                  onClick={() => handleBoardFilter(board.id)}
                  style={{
                    padding: '8px 16px',
                    background: selectedBoard === board.id ? '#748320' : '#f0f0f0',
                    color: selectedBoard === board.id ? '#fff' : '#333',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {board.name} ({count})
                </button>
              );
            })}
          </div>
        )}

        {/* 검색 결과 */}
        {filteredResults.length === 0 ? (
          <div style={{padding: '60px', textAlign: 'center', color: '#999'}}>
            <p style={{fontSize: '16px', marginBottom: '10px'}}>검색 결과가 없습니다.</p>
            <p style={{fontSize: '14px'}}>다른 검색어로 시도해보세요.</p>
          </div>
        ) : (
          <table style={{width: '100%', borderCollapse: 'collapse'}}>
            <thead>
              <tr style={{borderBottom: '2px solid #ddd'}}>
                <th style={{padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333'}}>제목</th>
                <th style={{padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#333', width: '120px'}}>작성자</th>
                <th style={{padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#333', width: '120px'}}>작성일</th>
              </tr>
            </thead>
            <tbody>
              {filteredResults.map((post) => (
                <tr key={post.id} style={{borderBottom: '1px solid #eee'}}>
                  <td style={{padding: '12px'}}>
                    <Link to={`/board/${post.board_id}/${post.id}`} style={{color: '#333', textDecoration: 'none', fontSize: '14px'}}>
                      {post.title}
                    </Link>
                  </td>
                  <td style={{padding: '12px', textAlign: 'center', fontSize: '14px', color: '#666'}}>{post.author}</td>
                  <td style={{padding: '12px', textAlign: 'center', fontSize: '14px', color: '#666'}}>{formatDate(post.date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default SearchResults;
